import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { getLang, useT } from '../lib/i18n';
import { haptic } from '../lib/telegram';
import { PinnedSheet } from './SetSheet';

/**
 * «Поделиться окнами»: the coach's free times as an Instagram story.
 *
 * The preview is the PNG drawn small: the server's own layout (view and
 * scale from /api/coach/story/preview, coach/story.ts), the same words and
 * colours. A time tapped in it is left out; what is left out is listed
 * under the preview to be put back. «Отправить мне» has the bot send the
 * 1080×1920 PNG and the coach's link to their chat.
 */

type Api = <T>(path: string, init?: RequestInit) => Promise<T>;
type Week = 'this' | 'next';
type Theme = 'dark' | 'light';

interface Chip { startsAt?: string; t: string; hot: boolean }
interface Preview {
  range: string;
  copy: { title: [string, string]; legend: string; cta: string };
  view: { days: { dow: string; date: string; slots: Chip[]; more: number }[] };
  scale: number;
  hidden: { startsAt: string; t: string; hot: boolean; date: string }[];
}

/** theme-v2's tokens, as on the PNG. */
const THEMES = {
  dark: { bg: '#000000', text: '#f3efe6', hint: 'rgba(243,239,230,.6)', line: 'rgba(243,239,230,.14)', accent: '#c9a86a', fire: '#e8590c' },
  light: { bg: '#fbfaf8', text: '#15161a', hint: '#6b6f76', line: 'rgba(0,0,0,.12)', accent: '#8a6a28', fire: '#c2490a' },
} as const;

/** The PNG's faces, for the preview only: the rest of the app keeps its own. */
function useStoryFonts() {
  useEffect(() => {
    if (document.getElementById('story-fonts')) return;
    const link = document.createElement('link');
    link.id = 'story-fonts';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=Jost:wght@400&display=swap';
    document.head.appendChild(link);
  }, []);
}

/**
 * The Day view's full-width button, or — `compact`, in the «Неделя»
 * header beside the arrows — a small one with the same name. `week`: the
 * week the sheet opens on (the one on screen, when it is this or next).
 */
export function ShareStoryButton({ api, compact, week }: { api: Api; compact?: boolean; week?: Week }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      {compact ? (
        <button className="story-open--compact" aria-label={t('story.button')} title={t('story.button')}
          onClick={() => { haptic.tap(); setOpen(true); }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3v12M7 8l5-5 5 5" /><path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
          </svg>
        </button>
      ) : (
        <button className="story-open" onClick={() => { haptic.tap(); setOpen(true); }}>{t('story.button')}</button>
      )}
      {open && <ShareStorySheet api={api} week={week} onClose={() => setOpen(false)} />}
    </>
  );
}

function ShareStorySheet({ api, week: startWeek, onClose }: { api: Api; week?: Week; onClose: () => void }) {
  const t = useT();
  useStoryFonts();
  const [week, setWeek] = useState<Week>(startWeek ?? 'this');
  const [theme, setTheme] = useState<Theme>('dark');
  const [hidden, setHidden] = useState<string[]>([]);
  const [data, setData] = useState<Preview | null>(null);
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | string>('idle');

  useEffect(() => {
    let live = true;
    api<Preview>('/api/coach/story/preview', { method: 'POST', body: JSON.stringify({ week, hidden }) })
      .then((d) => { if (live) setData(d); })
      .catch((e: Error) => { if (live) setState(t('co.failed', { message: e.message })); });
    return () => { live = false; };
  }, [week, hidden]);

  const toggle = (startsAt?: string) => {
    if (!startsAt) return;
    haptic.tap();
    setState('idle');
    setHidden((h) => (h.includes(startsAt) ? h.filter((x) => x !== startsAt) : [...h, startsAt]));
  };
  const pickWeek = (w: Week) => { if (w === week) return; haptic.tap(); setWeek(w); setHidden([]); setData(null); setState('idle'); };

  async function send() {
    haptic.tap();
    setState('sending');
    try {
      await api('/api/coach/story/send', { method: 'POST', body: JSON.stringify({ week, theme, hidden }) });
      haptic.success();
      setState('sent');
    } catch (e) {
      const code = (e as Error).message;
      setState(code === 'nothing_free' ? t('story.empty') : code === 'no_link' ? t('story.noLink') : t('co.failed', { message: code }));
    }
  }

  const empty = data !== null && data.view.days.length === 0;
  const dayName = (date: string) => new Date(`${date}T12:00:00Z`)
    .toLocaleDateString(getLang() === 'ru' ? 'ru-RU' : 'en-GB', { weekday: 'short', day: 'numeric', timeZone: 'UTC' });

  return (
    <PinnedSheet title={t('story.title')} onClose={onClose} full
      footer={<>
        {state !== 'idle' && state !== 'sending' && (
          <div className={`story-note${state === 'sent' ? ' story-note--ok' : ''}`}>{state === 'sent' ? t('story.sent') : state}</div>
        )}
        <button className="savebtn" disabled={!data || empty || state === 'sending'} onClick={send}>
          {state === 'sending' ? t('story.sending') : t('story.send')}
        </button>
      </>}>
      <div className="bseg" role="radiogroup" aria-label={t('story.title')}>
        {(['this', 'next'] as const).map((w) => (
          <button key={w} role="radio" aria-checked={week === w} onClick={() => pickWeek(w)}>
            {t(w === 'this' ? 'story.thisWeek' : 'story.nextWeek')}
          </button>
        ))}
      </div>
      <div className="bseg" role="radiogroup" aria-label={`${t('story.dark')} / ${t('story.light')}`}>
        {(['dark', 'light'] as const).map((x) => (
          <button key={x} role="radio" aria-checked={theme === x} onClick={() => { haptic.tap(); setTheme(x); }}>
            {t(x === 'dark' ? 'story.dark' : 'story.light')}
          </button>
        ))}
      </div>
      {!data ? <div className="list"><div className="skeleton story-skeleton" /></div>
        : empty ? <div className="empty" style={{ padding: '28px' }}>{t('story.empty')}</div>
        : <>
          <div className="hot-hint">{t('story.hint')}</div>
          <StoryPreview data={data} theme={theme} onTap={toggle} />
          {data.hidden.length > 0 && <>
            <div className="hot-hint">{t('story.hiddenTitle')}</div>
            <div className="hot-chips">
              {data.hidden.map((h) => (
                <button key={h.startsAt} className="hot-chip story-back" onClick={() => toggle(h.startsAt)}>
                  {dayName(h.date)} · {h.t}
                </button>
              ))}
            </div>
          </>}
        </>}
    </PinnedSheet>
  );
}

/** The 1080×1920 story, scaled to the sheet's width. Sizes as in coach/story.ts. */
function StoryPreview({ data, theme, onTap }: { data: Preview; theme: Theme; onTap: (startsAt?: string) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [k, setK] = useState(0.3);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => setK(el.clientWidth / 1080);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const c = THEMES[theme];
  const s = data.scale;
  const head = "'Inter Tight', system-ui, sans-serif";
  const chip = (kind: 'plain' | 'hot' | 'more'): CSSProperties => ({
    fontFamily: head, fontSize: 44 * s, fontWeight: 600, lineHeight: 1.21,
    padding: `${14 * s}px ${28 * s}px`, borderRadius: 26 * s, background: 'transparent',
    border: kind === 'hot' ? `4px solid ${c.fire}` : `2px solid ${c.line}`,
    color: kind === 'hot' ? c.fire : kind === 'more' ? c.hint : c.text,
  });

  return (
    <div className="story-prev" ref={box} style={{ height: 1920 * k }}>
      <div style={{
        width: 1080, height: 1920, transform: `scale(${k})`, transformOrigin: '0 0', boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column', gap: 44, padding: '110px 96px 0', overflow: 'hidden',
        background: c.bg, color: c.text, fontFamily: head, fontWeight: 400, lineHeight: 1.21,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <svg width="400" height="86" viewBox="0 0 560 120" role="img" aria-label="PadelDesk">
            <rect x="20" y="26" width="80" height="68" rx="10" fill="none" stroke={c.text} strokeWidth="2.2" />
            <line x1="34" y1="46" x2="62" y2="46" stroke={c.text} strokeWidth="2" strokeLinecap="round" />
            <line x1="34" y1="58" x2="56" y2="58" stroke={c.text} strokeWidth="2" strokeLinecap="round" />
            <circle cx="78" cy="70" r="11" fill={c.accent} />
            <path d="M71 62c4 5 4 11 0 16M85 62c-4 5-4 11 0 16" fill="none" stroke={c.bg} strokeWidth="1.6" strokeLinecap="round" />
            <text x="130" y="73" fontFamily="Jost, Futura, 'Century Gothic', sans-serif" fontWeight="400" fontSize="34" letterSpacing="10" fill={c.text}>PADEL DESK</text>
          </svg>
          <div style={{ fontFamily: head, fontSize: 124, lineHeight: 0.98, fontWeight: 700, letterSpacing: -2.48 }}>
            {data.copy.title[0]}<br />{data.copy.title[1]}
          </div>
          <div style={{ fontFamily: head, fontSize: 50, fontWeight: 500, color: c.hint }}>{data.range}</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 30 * s }}>
          {data.view.days.map((d) => (
            <div key={d.date} style={{ display: 'flex', alignItems: 'flex-start', gap: 32 * s, borderTop: `2px solid ${c.line}`, paddingTop: 28 * s }}>
              <div style={{ width: 170 * s, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 4 * s }}>
                <div style={{ fontSize: 30 * s, letterSpacing: 6 * s, fontWeight: 600, color: c.hint }}>{d.dow}</div>
                <div style={{ fontSize: 58 * s, fontWeight: 700, lineHeight: 1 }}>{d.date}</div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 * s, paddingTop: 6 * s, flex: 1 }}>
                {d.slots.map((x) => (
                  <button key={x.startsAt ?? x.t} style={chip(x.hot ? 'hot' : 'plain')} onClick={() => onTap(x.startsAt)}>{x.t}</button>
                ))}
                {d.more > 0 && <div style={chip('more')}>+{d.more}</div>}
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingTop: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 32, color: c.hint }}>
              <div style={{ width: 26, height: 26, borderRadius: 13, border: `4px solid ${c.fire}`, boxSizing: 'border-box' }} />
              <div>{data.copy.legend}</div>
            </div>
            <div style={{ fontSize: 44, fontWeight: 600, color: c.accent }}>{data.copy.cta}</div>
          </div>
        </div>
        {/* Below: empty, for Instagram's link sticker. */}
      </div>
    </div>
  );
}
